import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { LazyLoadEvent, MessageService } from 'primeng/api';
import { MultiSelect } from 'primeng/multiselect';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { capitalizeWords } from '../../../assets/functions/capitalize-words';
import { createWorkbookBuffer } from '../../../assets/functions/exceljs-export';
import { FormatOdometerNumber } from '../../../assets/functions/format-odometer-number';
import { getHeaderMap } from '../../../assets/functions/header-map-excel';
import { maskCpfAndCnpj } from '../../../assets/functions/information-masks';
import { fileTypeTicket, listStatusType } from '../../../assets/functions/list-status-type';
import { OnlyAcceptNumbers } from '../../../assets/functions/only-accept-numbers';
import { validationCnpj, validationCpf } from '../../../assets/validations/cpf_cnpj_validation';
import { ApsPermitedScreen } from '../../models/aps-permission/aps-permited-screen';
import { ConstantsFordProtect } from '../../models/constants/constants-ford-protect';
import { ContractCancelInput } from '../../models/contract/contract-cancel-input';
import { ExportContractFromExcelBusinessBra } from '../../models/contract/export-contract-from-excel-business-bra';
import { FilterContractSearch } from '../../models/contract/filter-contract-search';
import { FilterContractSearchResult } from '../../models/contract/filter-contract-search-result';
import { FilterContractSearchResultSimplified } from '../../models/contract/filter-contract-search-result-simplified';
import { FilterContractSearchResultTotalRecord } from '../../models/contract/filter-contract-search-result-total-record';
import { SalesTypeFord } from '../../models/contract/sales-type-ford';
import { Term } from '../../models/contract/term';
import { TermGeneration } from '../../models/contract/term-generation';
import { FileType } from '../../models/enum/file-type';
import { OperationCode } from '../../models/enum/operation-code';
import { StatusContract } from '../../models/enum/status-contract';
import { ProductActive } from '../../models/product/product-active';
import { ApsPermissionService } from '../../services/aps-permission/aps-permission.service';
import { ContractService } from '../../services/contract/contract.service';
import { ProductService } from '../../services/product/product.service';
import { SystemCountryService } from '../../services/system-country/system-country.service';
import { WorkbookColumn } from '../../../assets/functions/exceljs-export';

type TableLazyLoadEventLike = { first?: number | null; rows?: number | null };
type HideablePanel = { hide: () => void };
type DocumentType = 'TERM' | 'TICKET';
type SearchFieldId = 'cpfCnpjSearch' | 'vinSearch' | 'contractOriginalNumberSearch' | 'dealerSearch';
type CancellationFieldId =
	| 'cancellationDateModalSearch'
	| 'reasonDropdownSearch'
	| 'carMileageSearch'
	| 'dealerEmailSearch'
	| 'sellerEmailSearch'
	| 'observationsSearch';

@Component({
	selector: 'app-contract-search',
	templateUrl: './contract-search.component.html',
	styleUrls: ['./contract-search.component.scss'],
	standalone: false
})
export class ContractSearchComponent implements OnInit {
	@ViewChild('countrySearchMulti') countrySearchMulti?: MultiSelect;
	private readonly destroyRef = inject(DestroyRef);
	private readonly searchContext = 'business' as const;

	pt = {
		firstDayOfWeek: 0,
		dayNames: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
		dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'],
		dayNamesMin: ['Do', 'Se', 'Te', 'Qu', 'Qu', 'Se', 'Sa'],
		monthNames: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho',
			'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
		monthNamesShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
		today: 'Hoje',
		clear: 'Limpar'
	};

	es = {
		firstDayOfWeek: 0,
		dayNames: ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sábado'],
		dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'],
		dayNamesMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'],
		monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio',
			'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
		monthNamesShort: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
		today: 'Hoy',
		clear: 'Limpar'
	};

	calendarLang = this.pt;

	statusFilter = [
		{ label: 'TRANSLATE.ACTIVE', value: StatusContract.ACTIVE },
		{ label: 'TRANSLATE.BLOCKED', value: StatusContract.BLOCKED },
		{ label: 'TRANSLATE.CANCELED', value: StatusContract.CANCELED },
		{ label: 'TRANSLATE.FINISHED', value: StatusContract.FINISHED },
		{ label: 'TRANSLATE.CREATED', value: StatusContract.CREATED },
		{ label: 'TRANSLATE.PENDING.PAYMENT', value: StatusContract.PENDING_PAYMENT },
		{ label: 'TRANSLATE.PENDING.CANCELLATION', value: StatusContract.PENDING_CANCELLATION },
		{ label: 'TRANSLATE.PENDING.ACTIVATION', value: StatusContract.PENDING_ACTIVATION },
		{ label: 'TRANSLATE.PENDING.AMENDMENT', value: StatusContract.PENDING_AMENDMENT },
		{ label: 'TRANSLATE.WITH.ERROR', value: StatusContract.WITH_ERROR }
	];

	globalContractFilter = [
		{ label: 'TRANSLATE.SELECT', value: 0 },
		{ label: 'TRANSLATE.YES', value: 1 },
		{ label: 'TRANSLATE.NOT', value: 2 }
	];

	contractStatus = [
		StatusContract.ACTIVE,
		StatusContract.PENDING_PAYMENT,
	];

	blockedUi = false;
	isPermissionLoading = false;
	salesTypeFords: SalesTypeFord[] = [];
	salesTypeFord!: SalesTypeFord;

	productActiveCountries: ProductActive[] = [];
	productActive!: ProductActive;

	filterContractSearchResult!: FilterContractSearchResult | null;
	filterContractSearch: FilterContractSearch;
	filterContractSearchResultList: FilterContractSearchResult[] = [];
	filterContractSearchResultTotalRecord: FilterContractSearchResultTotalRecord;
	isSubmitted = false;
	isSubmittedExcel = false;
	isPermission = false;
	canRequestCancellation = false;
	prebContract: string = '';
	contractCountryCode = '';
	hasTriedFilterSubmit = false;

	display = false;
	displayModalPdf = false;
	cancellationDateModal: string | null = null;
	selectedReason: string | null = null;
	idProductNameModal: string | null = null;
	chassiModal: string | null = null;
	customerNameModal: string | null = null;
	readonly DOCUMENT_TYPE_TERM: DocumentType = 'TERM';
	readonly DOCUMENT_TYPE_TICKET: DocumentType = 'TICKET';
	documentType: DocumentType | null = null;
	showDocumentTypeError = false;
	isDownloadingPdf = false;
	isSubmittingCancellation = false;
	hasTriedCancellationSubmit = false;
	contractId!: number | null;
	productKey: number = 0;
	dealerEmail: string | null = null;
	sellerEmail: string | null = null;
	sellerCode: string | null = null;
	termGeneration!: TermGeneration | null;
	termFileId!: number | null;
	termTypeFile!: number | null;
	ticketFileId!: number | null;
	ticketTypeFile!: number | null;
	fileNameType: string = '';
	coverageKey: number = 0;
	vinSaleDateComparison!: Date;
	systemCountry: string = '';
	zeroMileage: number | null = null;
	carMileage: number | null = null;
	observations: string | null = null;
	readonly cancellationEmailMaxLength = 50;
	readonly cancellationMileageMaxLength = 7;
	readonly cancellationObservationsMaxLength = 500;
	readonly searchDocumentMaxLength = 14;
	readonly searchVinLength = 17;
	readonly searchOriginalContractLength = 10;
	readonly searchDealerMaxLength = 5;
	private readonly emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

	cancellationReason = [
		{ label: 'TRANSLATE.WITHDRAWAL', value: ConstantsFordProtect.WITHDRAWAL },
		{ label: 'TRANSLATE.CHARGEBACK', value: ConstantsFordProtect.CHARGEBACK },
		{ label: 'TRANSLATE.THEFT', value: ConstantsFordProtect.THEFT },
		{ label: 'TRANSLATE.TOTAL.LOSS', value: ConstantsFordProtect.TOTAL_LOSS },
		{ label: 'TRANSLATE.OTHER', value: ConstantsFordProtect.OTHER }
	];

	contractStatusCode = StatusContract.PENDING_CANCELLATION;
	displayUndoCancel = false;
	contractIDUndoCancellation: number | undefined = 0;
	vinUndoCancellation: string | undefined = '';
	productIDUndoCancellation: number | undefined = 0;
	disableReactivateContract = false;
	private countryPanelCloseTimeout: ReturnType<typeof setTimeout> | null = null;
	private readonly countryPanelCloseDelayMs = 4000;
	countriesList: { label: string; value: string }[] = [];

	constructor(
		private translateService: TranslateService,
		private messageService: MessageService,
		private router: Router,
		private apsPermissionService: ApsPermissionService,
		private contractService: ContractService,
		private productService: ProductService,
		private systemCountryService: SystemCountryService,
		private onlyAcceptNumbers: OnlyAcceptNumbers,
		private formatDistance: FormatOdometerNumber,

	) {

		this.filterContractSearch = new FilterContractSearch();
		this.filterContractSearchResultTotalRecord = new FilterContractSearchResultTotalRecord();
		this.systemCountryService.setSystemCountry();
		this.isPermissionLoading = !this.apsPermissionService.hasResolvedPermissions();

		this.buttonClean();
	}

	//Revisar aqui - Doble carga de SalesTypeFord dropdown
	async ngOnInit() {
		this.isPermissionLoading = !this.apsPermissionService.hasResolvedPermissions();

		await this.apsPermissionService.bootstrapPermissions();

		this.systemCountry = this.systemCountryService.getSystemCountry();
		this.isPermission = this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenContractSearch);
		this.canRequestCancellation =
			this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenContractCancellation) ||
			this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenProcessContractCancel);
		const shouldRestoreFromEdit = this.shouldRestoreFromEdit();
		const savedFilter = shouldRestoreFromEdit
			? this.contractService.getFilterContractSearchByContext(this.searchContext)
			: undefined;

		if (!shouldRestoreFromEdit) {
			this.contractService.clearFilterContractSearchByContext(this.searchContext);
			this.buttonClean();
		}

		this.initializeCountriesList();

		if (this.isPermission) {
			if (shouldRestoreFromEdit && savedFilter) {
				this.filterContractSearch = savedFilter;
				// Ensure countries is a valid array (handle undefined from saved filters)
				if (!this.filterContractSearch.countries) {
					this.filterContractSearch.countries = [];
				}
				// If countries are already set in saved filter, load products and sales types for them
				if (this.filterContractSearch.countries.length > 0) {
					this.searchProductActiveCountry();
					this.searchSalesTypeFord();
				}
			} else {
				// Only initialize with defaults when NOT restoring from previous filter
				this.productActive = this.addProductActiveDefault();
				this.salesTypeFord = this.addSalesTypeFordDefault();
				this.searchSalesTypeFord();
			}

			// Limpar dados da tabela ao iniciar página
			// Apenas se não estiver restaurando do filtro anterior
			if (!shouldRestoreFromEdit) {
				this.filterContractSearchResultTotalRecord.filterContractSearchResultSimplifiedDtos = [];
				this.filterContractSearchResultTotalRecord.totalRecord = 0;
			} else {
				// Se restaurando do filtro anterior, executar a busca automaticamente
				// Usa restoreSearchFromSavedFilter para não depender dos objetos UI (salesTypeFord/productActive)
				// que podem ainda estar como default (id=0) por serem carregamentos assíncronos
				if (savedFilter && this.filterContractSearch.countries && this.filterContractSearch.countries.length > 0) {
					setTimeout(() => {
						this.restoreSearchFromSavedFilter();
					}, 0);
				}
			}
		}

		this.updateCalendarLanguage(this.translateService.currentLang || this.translateService.defaultLang || ConstantsFordProtect.PT_BR);

		this.translateService.onLangChange.pipe(
			takeUntilDestroyed(this.destroyRef)
		).subscribe((langChange: LangChangeEvent) => {
			this.updateCalendarLanguage(langChange.lang);
			this.initializeCountriesList();
		});

		this.isPermissionLoading = false;

	}

	private updateCalendarLanguage(language: string): void {
		this.calendarLang = language.toLowerCase().startsWith('es') ? this.es : this.pt;
	}

	private shouldRestoreFromEdit(): boolean {
		const state = history.state as { restoreSearch?: boolean; source?: string };
		return state?.restoreSearch === true && state?.source === 'contract-edit';
	}

	private showCancellationError(error: HttpErrorResponse, context?: Record<string, unknown>): void {
		const status = error?.status;
		const reference = this.buildCancellationErrorReference(error);
		const backendDetails = (
			error?.error?.message ||
			error?.error?.exceptionMessage ||
			error?.error?.trace ||
			error?.message ||
			''
		).toString().toLowerCase();

		const isAigNotFound =
			backendDetails.includes('aigbrasilcontractcancelexception') ||
			backendDetails.includes('não encontrado para cancelamento') ||
			backendDetails.includes('nao encontrado para cancelamento');

		const isCredentialMismatch =
			backendDetails.includes('acesso inválido') ||
			backendDetails.includes('acesso invalido') ||
			backendDetails.includes('credenciais não conferem') ||
			backendDetails.includes('credenciais nao conferem');

		if (status === 401) {
			this.clearModalCancel();
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
			});
			return;
		}

		if (isAigNotFound) {
			const partialCancellationMessage = this.translateService.instant('TRANSLATE.CANCELLATION.PARTIAL.PROCESS.WARNING');
			const partialCancellationDetail = reference
				? `${partialCancellationMessage} ${this.translateService.instant('TRANSLATE.SUPPORT.REFERENCE')}: ${reference}.`
				: partialCancellationMessage;

			// Solicitação já registrada internamente - fechar modal e atualizar lista
			// evita que o usuário reenvie a mesma solicitação
			this.cleanFilters();
			this.filterValidation();
			this.messageService.add({
				severity: 'warn',
				summary: this.translateService.instant('TRANSLATE.ATTENTION'),
				detail: partialCancellationDetail
			});
			console.warn('Cancellation returned integration warning.', { reference, error });
			return;
		}

		if (isCredentialMismatch) {
			const supportDetail = this.appendCancellationSupportReference(
				this.translateService.instant('TRANSLATE.CANCELLATION.INTEGRATION.CREDENTIALS.ERROR'),
				reference
			);

			this.clearModalCancel();
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: supportDetail
			});
			console.error('Cancellation rejected by integration credentials.', { reference, status, context, error });
			return;
		}

		switch (status) {
			case 400:
			case 404:
			case 500:
				this.clearEditableCancellationFields();
				this.messageService.add({
					severity: 'error',
					summary: this.translateService.instant('TRANSLATE.ERROR'),
					detail: this.appendCancellationSupportReference(
						this.translateService.instant('TRANSLATE.UNABLE.TO.REQUEST.CANCELLATION'),
						reference
					)
				});
				console.error('Cancellation request failed before completion.', { reference, status, context, error });
				break;

			default: {
				const genericErrorDetail = this.appendCancellationSupportReference(
					this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR'),
					reference
				);

				this.clearEditableCancellationFields();
				this.messageService.add({
					severity: 'error',
					summary: this.translateService.instant('TRANSLATE.ERROR'),
					detail: genericErrorDetail
				});
				console.error('Cancellation failed with unexpected error.', { reference, status, context, error });
				break;
			}
		}
	}

	clearEditableCancellationFields(): void {
		this.isSubmittingCancellation = false;
		this.hasTriedCancellationSubmit = false;
		this.cancellationDateModal = null;
		this.selectedReason = null;
		this.zeroMileage = null;
		this.carMileage = null;
		this.observations = null;
		this.dealerEmail = null;
		this.sellerEmail = null;
	}

	private appendCancellationSupportReference(detail: string, reference: string): string {
		return reference
			? `${detail} ${this.translateService.instant('TRANSLATE.SUPPORT.REFERENCE')}: ${reference}.`
			: detail;
	}

	private buildCancellationErrorReference(error: HttpErrorResponse): string {
		const backendTimestamp = error?.error?.timestamp;
		if (backendTimestamp) {
			return String(backendTimestamp);
		}

		const correlationId =
			error?.headers?.get?.('x-correlation-id') ||
			error?.headers?.get?.('x-request-id');

		if (correlationId) {
			return correlationId;
		}

		return String(Date.now());
	}

	private initializeCountriesList(): void {
		this.countriesList = [
			{ label: this.translateService.instant('TRANSLATE.CHOOSE.COUNTRY.ARG'), value: ConstantsFordProtect.ARG },
			{ label: this.translateService.instant('TRANSLATE.CHOOSE.COUNTRY.BRA'), value: ConstantsFordProtect.BRA },
			{ label: this.translateService.instant('TRANSLATE.CHOOSE.COUNTRY.CHL'), value: ConstantsFordProtect.CHL },
			{ label: this.translateService.instant('TRANSLATE.CHOOSE.COUNTRY.COL'), value: ConstantsFordProtect.COL },
			{ label: this.translateService.instant('TRANSLATE.CHOOSE.COUNTRY.VEN'), value: ConstantsFordProtect.VEN },
		];
	}

	filterValidation() {
		this.hasTriedFilterSubmit = true;

		if (this.filterContractSearch.document === '' && this.filterContractSearch.vin === ''
			&& this.filterContractSearch.dealer === '' && this.filterContractSearch.contractOriginalNumber === null
			&& this.filterContractSearch.contractDateStart === null && this.filterContractSearch.contractDateEnd === null
			&& this.filterContractSearch.validityDateStart === null && this.filterContractSearch.validityDateEnd === null
			&& this.salesTypeFord.id === 0 && this.productActive.id === 0 && this.filterContractSearch.contractStatus === 0
			&& !this.filterContractSearch.countries && this.filterContractSearch.globalContract === 0) {

			this.messageService.add({
				severity: 'error', summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.FILTER.NOT.SELECTED')
			});
		} else if (!this.filterContractSearch.countries || this.filterContractSearch.countries.length === 0) {
			this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Selecione o(s) país(es).' });
		} else {
			const invalidField = this.validateSearchFilters();

			if (invalidField) {
				this.focusSearchField(invalidField);
				return;
			}

			this.buttonFilterSearch();
		}
	}

	buttonFilterSearch() {
		if (this.isSubmitted) {
			return;
		}

		if (this.validateSearchFilters()) {
			return;
		}

		this.isSubmitted = true;
		this.blockedUi = true;
		const searchPayload = this.buildSearchPayloadFromCurrentFilters();

		this.contractService.contractSearch(searchPayload)
			.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: FilterContractSearchResultTotalRecord) => {

				this.filterContractSearchResultTotalRecord = response;
				this.isSubmitted = false;
				this.blockedUi = false;
			}, (err) => {

				if (err.status === 401) {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
					});

				} else {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
					});
				}

				this.isSubmitted = false;
				this.blockedUi = false;
			});
	}

	// Executa busca ao restaurar filtro anterior sem sobrescrever os IDs salvos
	// (salesTypeFordId e productId) com os objetos da UI que podem estar como default
	private restoreSearchFromSavedFilter(): void {
		if (this.isSubmitted) return;

		if (this.validateSearchFilters()) {
			return;
		}

		this.isSubmitted = true;
		this.blockedUi = true;
		const searchPayload = this.buildSearchPayloadFromSavedFilters();

		this.contractService.contractSearch(searchPayload)
			.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: FilterContractSearchResultTotalRecord) => {

				this.filterContractSearchResultTotalRecord = response;
				this.isSubmitted = false;
				this.blockedUi = false;
			}, (err) => {

				if (err.status === 401) {
					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
					});
				} else {
					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
					});
				}

				this.isSubmitted = false;
				this.blockedUi = false;
			});
	}

	loadData(event: TableLazyLoadEventLike | LazyLoadEvent | null | undefined) {
		if (this.isSubmitted) {
			return;
		}

		if (event && event.first !== undefined && event.first !== null && event.first > 0 && event.rows !== undefined && event.rows !== null) {
			this.filterContractSearch.numberPage = (event.first / event.rows);
			this.filterValidationLazyLoad();
		} else if (event && event.first !== undefined && event.first !== null) {
			this.filterContractSearch.numberPage = event.first;
			this.filterValidationLazyLoad();
		}
	}

	filterValidationLazyLoad() {
		// Se não há país selecionado (obrigatório), não faz a busca
		if (!this.filterContractSearch.countries || this.filterContractSearch.countries.length === 0) {
			return;
		}

		// Se chegou aqui, há um país selecionado. Verificar se há algum filtro
		if (this.filterContractSearch.document === '' && this.filterContractSearch.vin === ''
			&& this.filterContractSearch.dealer === ''
			&& (this.filterContractSearch.contractOriginalNumber === '' || this.filterContractSearch.contractOriginalNumber === null)
			&& this.filterContractSearch.contractDateStart === null && this.filterContractSearch.contractDateEnd === null
			&& this.filterContractSearch.validityDateStart === null && this.filterContractSearch.validityDateEnd === null
			&& this.salesTypeFord.id === 0 && this.productActive.id === 0 && this.filterContractSearch.contractStatus === 0
			&& this.filterContractSearch.globalContract === 0) {
			// Sem filtros além do país, não faz busca
			return;
		}

		// Há filtros e país selecionado, fazer busca
		this.buttonFilterSearch();
	}

	buttonClean() {

		this.filterContractSearch = new FilterContractSearch();
		this.hasTriedFilterSubmit = false;
		this.filterContractSearchResultTotalRecord = new FilterContractSearchResultTotalRecord();
		// Garantir que a tabela fica vazia
		this.filterContractSearchResultTotalRecord.filterContractSearchResultSimplifiedDtos = [];
		this.filterContractSearchResultTotalRecord.totalRecord = 0;
		this.productActiveCountries = [];
		this.filterContractSearch.document = '';
		this.filterContractSearch.vin = '';
		this.filterContractSearch.dealer = '';
		this.filterContractSearch.countries = [];
		this.filterContractSearch.contractNumber = null;
		this.filterContractSearch.contractDateStart = null;
		this.filterContractSearch.contractDateEnd = null;
		this.filterContractSearch.globalContract = 0;
		this.filterContractSearch.contractStatus = 0;
		this.productActive = this.addProductActiveDefault();
		this.salesTypeFord = this.addSalesTypeFordDefault();
		this.resetCountrySearchFilterInput();
	}

	private resetCountrySearchFilterInput(): void {
		const countryMulti = this.countrySearchMulti as MultiSelect & {
			resetFilter?: () => void;
			filterValue?: string | null;
		};

		countryMulti?.resetFilter?.();

		if (countryMulti && 'filterValue' in countryMulti) {
			countryMulti.filterValue = null;
		}
	}

	onDocumentSearchInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		const sanitizedValue = this.sanitizeDocumentSearchValue(input.value);

		if (input.value !== sanitizedValue) {
			input.value = sanitizedValue;
		}

		this.filterContractSearch.document = sanitizedValue;
		this.hasTriedFilterSubmit = false;
	}

	onVinSearchInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		const sanitizedValue = this.normalizeAlphaNumericValue(input.value).slice(0, this.searchVinLength);

		if (input.value !== sanitizedValue) {
			input.value = sanitizedValue;
		}

		this.filterContractSearch.vin = sanitizedValue;
		this.hasTriedFilterSubmit = false;
	}

	onContractOriginalNumberInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		const sanitizedValue = this.normalizeDigitsValue(input.value).slice(0, this.searchOriginalContractLength);

		if (input.value !== sanitizedValue) {
			input.value = sanitizedValue;
		}

		this.filterContractSearch.contractOriginalNumber = sanitizedValue;
		this.hasTriedFilterSubmit = false;
	}

	onDealerSearchInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		const sanitizedValue = this.normalizeAlphaNumericValue(input.value).slice(0, this.searchDealerMaxLength);

		if (input.value !== sanitizedValue) {
			input.value = sanitizedValue;
		}

		this.filterContractSearch.dealer = sanitizedValue;
		this.hasTriedFilterSubmit = false;
	}

	private focusSearchField(fieldId: SearchFieldId): void {
		setTimeout(() => {
			document.getElementById(fieldId)?.focus();
		}, 0);
	}

	private validateSearchFilters(): SearchFieldId | null {
		if (this.isSearchDocumentInvalid()) {
			return 'cpfCnpjSearch';
		}

		if (this.isSearchVinInvalid()) {
			return 'vinSearch';
		}

		if (this.isSearchContractOriginalNumberInvalid()) {
			return 'contractOriginalNumberSearch';
		}

		if (this.isSearchDealerInvalid()) {
			return 'dealerSearch';
		}

		return null;
	}

	isSearchDocumentInvalid(): boolean {
		const rawDocument = (this.filterContractSearch.document || '').trim();

		if (!rawDocument) {
			return false;
		}

		const countries = this.getSearchDocumentCountries();

		if (countries.length === 0) {
			return false;
		}

		const normalizedDocument = this.normalizeDocumentSearchValue(rawDocument);

		if (!normalizedDocument) {
			return true;
		}

		return !countries.some(country => this.isDocumentValidForCountry(normalizedDocument, country));
	}

	isSearchVinInvalid(): boolean {
		const vin = (this.filterContractSearch.vin || '').trim();

		if (!vin) {
			return false;
		}

		return this.normalizeAlphaNumericValue(vin).length !== this.searchVinLength;
	}

	isSearchContractOriginalNumberInvalid(): boolean {
		const contractOriginalNumber = (this.filterContractSearch.contractOriginalNumber || '').trim();

		if (!contractOriginalNumber) {
			return false;
		}

		return !/^\d{1,10}$/.test(this.normalizeDigitsValue(contractOriginalNumber));
	}

	isSearchDealerInvalid(): boolean {
		const dealer = (this.filterContractSearch.dealer || '').trim();

		if (!dealer) {
			return false;
		}

		return !/^[A-Z0-9]{1,5}$/.test(this.normalizeAlphaNumericValue(dealer));
	}

	getSearchDocumentLabel(): string {
		if (this.hasMultipleSearchCountries()) {
			return 'Documento:';
		}

		switch (this.getSingleSearchCountry()) {
			case ConstantsFordProtect.BRA:
				return 'CPF/CNPJ:';
			case ConstantsFordProtect.ARG:
				return 'DNI/CUIT:';
			case ConstantsFordProtect.CHL:
				return 'RUN/RUT:';
			case ConstantsFordProtect.COL:
				return 'Cédula/NIT:';
			case ConstantsFordProtect.VEN:
				return 'Cédula/RIF:';
			default:
				return this.translateService.instant('TRANSLATE.TWO.POINTS.CPF.CNPJ');
		}
	}

	getSearchDocumentPlaceholder(): string {
		if (this.hasMultipleSearchCountries()) {
			return 'Documento';
		}

		switch (this.getSingleSearchCountry()) {
			case ConstantsFordProtect.BRA:
				return 'CPF/CNPJ';
			case ConstantsFordProtect.ARG:
				return 'DNI/CUIT';
			case ConstantsFordProtect.CHL:
				return 'RUN/RUT';
			case ConstantsFordProtect.COL:
				return 'Cédula/NIT';
			case ConstantsFordProtect.VEN:
				return 'Cédula/RIF';
			default:
				return this.translateService.instant('TRANSLATE.CPF.CNPJ');
		}
	}

	getSearchDocumentInputMaxLength(): number {
		const singleCountry = this.getSingleSearchCountry();

		switch (singleCountry) {
			case ConstantsFordProtect.BRA:
				return 18;
			case ConstantsFordProtect.ARG:
				return 11;
			case ConstantsFordProtect.CHL:
				return 9;
			case ConstantsFordProtect.COL:
				return this.searchDocumentMaxLength;
			case ConstantsFordProtect.VEN:
				return 10;
			default:
				return this.searchDocumentMaxLength;
		}
	}

	getSearchDocumentErrorKey(): string {
		const normalizedDocument = this.normalizeDocumentSearchValue(this.filterContractSearch.document || '');
		const singleCountry = this.getSingleSearchCountry();

		if (singleCountry === ConstantsFordProtect.BRA) {
			if (normalizedDocument.length === 11) {
				return 'TRANSLATE.CPF.INVALID';
			}

			if (normalizedDocument.length === 14) {
				return 'TRANSLATE.CNPJ.INVALID';
			}

			return 'TRANSLATE.FILL.CPF.CNPJ.CORRECTLY';
		}

		return 'TRANSLATE.INVALID.DOCUMENT';
	}

	private getSearchDocumentCountries(): string[] {
		return Array.isArray(this.filterContractSearch.countries)
			? this.filterContractSearch.countries.filter(Boolean)
			: [];
	}

	private getSingleSearchCountry(): string | null {
		const countries = this.getSearchDocumentCountries();
		return countries.length === 1 ? countries[0] : null;
	}

	private hasMultipleSearchCountries(): boolean {
		return this.getSearchDocumentCountries().length > 1;
	}

	private sanitizeDocumentSearchValue(value: string): string {
		const singleCountry = this.getSingleSearchCountry();
		const trimmedValue = (value || '').trimStart();

		if (singleCountry === ConstantsFordProtect.BRA) {
			const normalizedDocument = this.normalizeAlphaNumericValue(trimmedValue).slice(0, this.searchDocumentMaxLength);
			return maskCpfAndCnpj(normalizedDocument).slice(0, 18);
		}

		if (singleCountry === ConstantsFordProtect.ARG) {
			return this.normalizeAlphaNumericValue(trimmedValue).slice(0, 11);
		}

		if (singleCountry === ConstantsFordProtect.CHL) {
			return this.normalizeAlphaNumericValue(trimmedValue).slice(0, 9);
		}

		if (singleCountry === ConstantsFordProtect.COL) {
			return this.normalizeAlphaNumericValue(trimmedValue).slice(0, 10);
		}

		if (singleCountry === ConstantsFordProtect.VEN) {
			return this.normalizeAlphaNumericValue(trimmedValue).slice(0, 10);
		}

		return trimmedValue.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, this.searchDocumentMaxLength);
	}

	private normalizeDocumentSearchValue(value: string): string {
		return (value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
	}

	private normalizeAlphaNumericValue(value: string): string {
		return (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
	}

	private normalizeDigitsValue(value: string): string {
		return (value || '').replace(/\D/g, '');
	}

	private parsePositiveIdentifier(value: unknown): number {
		const normalizedValue = this.normalizeDigitsValue(String(value ?? ''));

		if (!normalizedValue) {
			return 0;
		}

		const parsedValue = Number.parseInt(normalizedValue, 10);
		return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
	}

	private buildSearchPayloadFromCurrentFilters(): FilterContractSearch {
		const searchPayload = this.buildNormalizedSearchPayload();
		searchPayload.salesTypeFordId = this.salesTypeFord.id;
		searchPayload.productId = this.productActive.id;
		searchPayload.operation = OperationCode.CAR;
		searchPayload.statusType = listStatusType().map(status => String(status));
		return searchPayload;
	}

	private buildSearchPayloadFromSavedFilters(): FilterContractSearch {
		const searchPayload = this.buildNormalizedSearchPayload();
		searchPayload.operation = OperationCode.CAR;
		searchPayload.statusType = listStatusType().map(status => String(status));
		return searchPayload;
	}

	private buildExcelSearchPayload(): FilterContractSearch {
		const searchPayload = this.buildSearchPayloadFromCurrentFilters();
		const normalizedOriginalContractNumber = this.normalizeDigitsValue(this.filterContractSearch.contractOriginalNumber || '');
		if (normalizedOriginalContractNumber) {
			searchPayload.contractNumber = this.parsePositiveIdentifier(normalizedOriginalContractNumber);
			searchPayload.contractOriginalNumber = '';
		}
		searchPayload.numberPage = 0;
		if (this.filterContractSearchResultTotalRecord?.totalRecord > 0) {
			searchPayload.rowsPage = Math.max(searchPayload.rowsPage || 20, this.filterContractSearchResultTotalRecord.totalRecord);
		}
		return searchPayload;
	}

	private buildNormalizedSearchPayload(): FilterContractSearch {
		const searchPayload = { ...this.filterContractSearch } as FilterContractSearch;
		searchPayload.countries = [...(this.filterContractSearch.countries || [])];
		searchPayload.statusType = [...(this.filterContractSearch.statusType || [])];
		searchPayload.document = this.buildSearchDocumentPayloadValue();
		searchPayload.vin = this.normalizeAlphaNumericValue(this.filterContractSearch.vin || '');
		searchPayload.dealer = this.normalizeAlphaNumericValue(this.filterContractSearch.dealer || '');
		searchPayload.contractOriginalNumber = this.normalizeDigitsValue(this.filterContractSearch.contractOriginalNumber || '');
		searchPayload.contractNumber = this.parsePositiveIdentifier(searchPayload.contractOriginalNumber);

		return searchPayload;
	}

	private buildSearchDocumentPayloadValue(): string {
		const rawDocument = this.filterContractSearch.document || '';
		const singleCountry = this.getSingleSearchCountry();

		if (singleCountry === ConstantsFordProtect.BRA) {
			return this.normalizeDocumentSearchValue(rawDocument);
		}

		if (singleCountry === ConstantsFordProtect.COL) {
			return this.normalizeDocumentSearchValue(rawDocument);
		}

		return this.normalizeDocumentSearchValue(rawDocument);
	}

	private isDocumentValidForCountry(document: string, country: string): boolean {
		switch (country) {
			case ConstantsFordProtect.BRA:
				if (/^\d{11}$/.test(document)) {
					return validationCpf(document);
				}

				if (/^[A-Z0-9]{14}$/.test(document)) {
					return validationCnpj(document);
				}

				return false;

			case ConstantsFordProtect.ARG:
				return /^[A-Z0-9]{7,11}$/.test(document);

			case ConstantsFordProtect.CHL:
				return /^[A-Z0-9]{9}$/.test(document);

			case ConstantsFordProtect.COL:
				return /^[A-Z0-9]{4,10}$/.test(document);

			case ConstantsFordProtect.VEN:
				return /^[A-Z0-9]{8,10}$/.test(document);

			default:
				return document.length > 0;
		}
	}

	exportExcel() {
		if (this.isSubmittedExcel) {
			return;
		}

		const invalidField = this.validateSearchFilters();

		if (invalidField) {
			this.hasTriedFilterSubmit = true;
			this.focusSearchField(invalidField);
			return;
		}

		if (this.filterContractSearch.document === '' && this.filterContractSearch.vin === ''
			&& this.filterContractSearch.dealer === '' && this.filterContractSearch.contractOriginalNumber === ''
			&& this.filterContractSearch.contractDateStart === null && this.filterContractSearch.contractDateEnd === null
			&& this.filterContractSearch.validityDateStart === null && this.filterContractSearch.validityDateEnd === null
			&& this.salesTypeFord.id <= 0 && this.productActive.id <= 0 && this.filterContractSearch.contractStatus <= 0
			&& !this.filterContractSearch.countries && this.filterContractSearch.globalContract === 0) {

			this.messageService.add({
				severity: 'error', summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.FILTER.NOT.SELECTED')
			});
		} else if (!this.filterContractSearch.countries || this.filterContractSearch.countries.length === 0) {

			this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Selecione o(s) país(es).' });

		}
		else {
			this.isSubmittedExcel = true;
			this.blockedUi = true;
			const searchPayload = this.buildExcelSearchPayload();

			this.contractService.contractSearchExcel(searchPayload)
				.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: ExportContractFromExcelBusinessBra[] | ExportContractFromExcelBusinessBra | null | undefined) => {
					const excelRows = this.normalizeExcelExportRows(response);

					if (excelRows.length > 0) {
						this.importXlsxFromArray(excelRows);
						return;
					}

					if (this.shouldAvoidExcelDetailFallback()) {
						this.handleExcelExportUnavailable();
						return;
					}

					this.importXlsxFromSearchResults(this.filterContractSearchResultTotalRecord?.filterContractSearchResultSimplifiedDtos || []);
				}, () => {
					if (this.shouldAvoidExcelDetailFallback()) {
						this.handleExcelExportUnavailable();
						return;
					}

					this.importXlsxFromSearchResults(this.filterContractSearchResultTotalRecord?.filterContractSearchResultSimplifiedDtos || []);
				});
		}
	}

	private normalizeExcelExportRows(response: ExportContractFromExcelBusinessBra[] | ExportContractFromExcelBusinessBra | null | undefined): ExportContractFromExcelBusinessBra[] {
		if (Array.isArray(response)) {
			return response.filter(row => !!row);
		}

		return response ? [response] : [];
	}

	private shouldAvoidExcelDetailFallback(): boolean {
		const normalizedDealer = this.normalizeAlphaNumericValue(this.filterContractSearch?.dealer || '');
		return normalizedDealer.length > 0;
	}

	private handleExcelExportUnavailable(): void {
		this.messageService.add({
			severity: 'error',
			summary: this.translateService.instant('TRANSLATE.ERROR'),
			detail: this.translateService.instant('TRANSLATE.EXCEL.CANNOT.BE.EXPORTED')
		});

		this.isSubmittedExcel = false;
		this.blockedUi = false;
	}

	private importXlsxFromSearchResults(rows: FilterContractSearchResultSimplified[]): void {
		const normalizedRows = Array.isArray(rows) ? rows : [];

		if (normalizedRows.length === 0) {
			this.handleExcelExportUnavailable();
			return;
		}

		forkJoin(normalizedRows.map(row => this.loadExportContractDetail(row)))
			.subscribe({
				next: resolvedRows => {
					this.importXlsxFromResolvedRows(resolvedRows, normalizedRows);
				},
				error: () => {
					this.importXlsxFromSummaryRows(normalizedRows);
				}
			});
	}

	private getExportContractDetailCandidateIds(row: Partial<FilterContractSearchResultSimplified>): number[] {
		const candidateIds: number[] = [];
		const appendCandidate = (value: unknown) => {
			const identifier = this.parsePositiveIdentifier(value);

			if (identifier > 0 && !candidateIds.includes(identifier)) {
				candidateIds.push(identifier);
			}
		};

		appendCandidate(row.contractKey);
		appendCandidate(row.contractOriginalNumber);
		appendCandidate(row.contractNumber);

		return candidateIds;
	}

	private loadExportContractDetail(row: Partial<FilterContractSearchResultSimplified>): Observable<FilterContractSearchResult | null> {
		const candidateIds = this.getExportContractDetailCandidateIds(row);

		const loadCandidate = (index: number): Observable<FilterContractSearchResult | null> => {
			if (index >= candidateIds.length) {
				return of(null);
			}

			return this.contractService.searchContractId(
				candidateIds[index],
				row.productKey || 0,
				row.coverageCategoryKey || 0
			).pipe(
				switchMap((response: FilterContractSearchResult | null) => response ? of(response) : loadCandidate(index + 1)),
				catchError(() => loadCandidate(index + 1))
			);
		};

		return loadCandidate(0);
	}

	private importXlsxFromResolvedRows(
		resolvedRows: (FilterContractSearchResult | null)[],
		summaryRows: FilterContractSearchResultSimplified[]
	): void {
		const rows = summaryRows.map((summaryRow, index) => {
			const resolvedRow = resolvedRows[index];
			return resolvedRow ? this.buildExcelRowFromContractDetail(resolvedRow) : this.buildSummaryFallbackExcelRow(summaryRow);
		});

		this.exportPreparedRows(rows);
	}

	private importXlsxFromSummaryRows(summaryRows: FilterContractSearchResultSimplified[]): void {
		this.exportPreparedRows(summaryRows.map(row => this.buildSummaryFallbackExcelRow(row)));
	}

	private buildExcelRowFromContractDetail(item: FilterContractSearchResult): Record<string, unknown> {
		const currency = this.getExcelCurrency(item.country);

		return {
			[this.translateService.instant('TRANSLATE.COUNTRY.EXCEL')]: item.country || this.getSingleSearchCountry() || '',
			[this.translateService.instant('TRANSLATE.CONTRACT.NUMBER.EXCEL')]: item.contractNumber || '',
			[this.translateService.instant('TRANSLATE.FP2.CODE.EXCEL')]: item.contractCodeFp2 ?? item.contractOriginalNumber ?? '',
			[this.translateService.instant('TRANSLATE.STATUS.EXCEL')]: this.statusName(Number(item.status || 0)),
			[this.translateService.instant('TRANSLATE.VIN.EXCEL')]: item.vin || '',
			[this.translateService.instant('TRANSLATE.PRODUCT.NAME.EXCEL')]: item.productName || '',
			[this.translateService.instant('TRANSLATE.PRODUCT.CODE.TITLE.EXCEL')]: item.productCode || '',
			[this.translateService.instant('TRANSLATE.LIMIT.EXCEL')]: item.limite || '',
			[this.translateService.instant('TRANSLATE.START.DATE.EXCEL')]: item.dateStart || null,
			[this.translateService.instant('TRANSLATE.END.DATE.EXCEL')]: item.coverageEndDate || item.contractDateEnd || null,
			[this.translateService.instant('TRANSLATE.CONTRACT.DATE.EXCEL')]: item.contractDateCreate || null,
			[this.translateService.instant('TRANSLATE.WSD.EXCEL')]: item.wsd || null,
			[this.translateService.instant('TRANSLATE.FINAL.CONTRACT.VALUE.EXCEL')]: this.formatExcelPrice(item.contractFinalPrice, currency),
			[this.translateService.instant('TRANSLATE.VIN.SELLER.PRICE.EXCEL')]: this.formatExcelPrice(item.vinSellerPrice, currency),
			[this.translateService.instant('TRANSLATE.COVERAGE.SELLER.COMMISSION.EXCEL')]: this.formatExcelPrice(item.dealerCommission, currency),
			[this.translateService.instant('TRANSLATE.CONTRACT.EVENT.CODE.EXCEL')]: this.translateContractEventCode(item.contractEventCode),
		};
	}

	private buildSummaryFallbackExcelRow(item: Partial<FilterContractSearchResultSimplified>): Record<string, unknown> {
		const contractNumbers = this.resolveFallbackContractNumbers(item);

		return {
			[this.translateService.instant('TRANSLATE.COUNTRY.EXCEL')]: item.country || this.getSingleSearchCountry() || '',
			[this.translateService.instant('TRANSLATE.CONTRACT.NUMBER.EXCEL')]: contractNumbers.contractNumber,
			[this.translateService.instant('TRANSLATE.FP2.CODE.EXCEL')]: contractNumbers.contractOriginalNumber,
			[this.translateService.instant('TRANSLATE.STATUS.EXCEL')]: this.statusName(Number(item.status || 0)),
			[this.translateService.instant('TRANSLATE.VIN.EXCEL')]: item.vin || '',
			[this.translateService.instant('TRANSLATE.PRODUCT.NAME.EXCEL')]: item.planName || '',
			[this.translateService.instant('TRANSLATE.LIMIT.EXCEL')]: item.limite || '',
			[this.translateService.instant('TRANSLATE.START.DATE.EXCEL')]: item.dateStart || null,
			[this.translateService.instant('TRANSLATE.END.DATE.EXCEL')]: item.coverageEndDate || null,
			[this.translateService.instant('TRANSLATE.PRODUCT.CODE.TITLE.EXCEL')]: item.productCode || '',
		};
	}

	private resolveFallbackContractNumbers(item: Partial<FilterContractSearchResultSimplified>): { contractNumber: string; contractOriginalNumber: string } {
		const currentSearchOriginalNumber = this.normalizeDigitsValue(this.filterContractSearch?.contractOriginalNumber || '');
		const contractNumber = String(item.contractNumber || '').trim();
		const contractOriginalNumber = String(item.contractOriginalNumber || '').trim();

		if (!contractOriginalNumber && currentSearchOriginalNumber && this.normalizeDigitsValue(contractNumber) === currentSearchOriginalNumber) {
			return {
				contractNumber: '',
				contractOriginalNumber: contractNumber
			};
		}

		return {
			contractNumber,
			contractOriginalNumber
		};
	}

	private exportPreparedRows(rows: Record<string, unknown>[]): void {
		if (rows.length === 0) {
			this.handleExcelExportUnavailable();
			return;
		}

		const excelColumns: WorkbookColumn[] = Object.keys(rows[0]).map(key => ({
			key,
			header: key
		}));

		void createWorkbookBuffer([
			{
				name: 'data',
				dataCellAlignment: { horizontal: 'right' },
				columns: excelColumns,
				rows
			}
		])
			.then(excelBuffer => {
				this.saveAsExcelFile(excelBuffer, 'contracts');
			})
			.catch(() => {
				this.handleExcelExportUnavailable();
			});
	}

	private getExcelCurrency(country: unknown): string {
		return country === ConstantsFordProtect.BRA ? 'R$' : '$';
	}

	private formatExcelPrice(value: unknown, currency: string): string {
		if (value === null || value === undefined || value === '') {
			return '';
		}

		return this.onlyAcceptNumbers.formattingFrontPrice(Number(value), currency);
	}

	private importXlsxFromArray(array: ExportContractFromExcelBusinessBra[]): void {
		const headerMap = getHeaderMap(this.translateService);
		const excelColumns = this.buildBusinessExcelColumns(headerMap);

		void createWorkbookBuffer([
			{
				name: 'data',
				dataCellAlignment: { horizontal: 'right' },
				columns: excelColumns,
				rows: array.map(item => this.buildBusinessExcelRow(item))
			}
		])
			.then(excelBuffer => {
				this.saveAsExcelFile(excelBuffer, 'contracts');
			})
			.catch(() => {
				this.messageService.add({
					severity: 'error', summary: this.translateService.instant('TRANSLATE.ERROR'),
					detail: this.translateService.instant('TRANSLATE.EXCEL.CANNOT.BE.EXPORTED')
				});

				this.isSubmittedExcel = false;
				this.blockedUi = false;
			});
	}

	private saveAsExcelFile(buffer: BlobPart, fileName: string): void {

		const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
		const EXCEL_EXTENSION = '.xlsx';
		const data: Blob = new Blob([buffer], {
			type: EXCEL_TYPE
		});

		saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
		this.messageService.add({
			severity: 'success',
			summary: this.translateService.instant('TRANSLATE.SUCCESS'),
			detail: this.translateService.instant('TRANSLATE.EXCEL.EXPORTED.SUCCESSFULLY')
		});
		this.isSubmittedExcel = false;
		this.blockedUi = false;
	}

	private buildBusinessExcelRow(item: ExportContractFromExcelBusinessBra): Record<string, unknown> {
		return {
			...item,
			contractEventCode: this.translateContractEventCode(item.contractEventCode)
		};
	}

	private buildBusinessExcelColumns(headerMap: Record<string, string>): WorkbookColumn[] {
		const numericColumns = new Set<string>([
			'contractCodeFp2',
			'odometerStart',
			'monthsFactoryWarranty',
			'productKey',
			'vinCustomerPrice',
			'contractFinalPrice',
			'vinSellerPrice',
			'dealerCommission',
			'costWarranty',
			'costService',
			'costTotal',
			'fordMargin',
			'cancelValue',
			'coverageMonths',
			'coverageKm',
			'coverageStartVisit',
			'coverageVisitLimit',
			'coverageVisitCounter',
			'paymentParcels',
			'parcelValue'
		]);
		const currencyColumns = new Set<string>([
			'vinCustomerPrice',
			'contractFinalPrice',
			'vinSellerPrice',
			'dealerCommission',
			'costWarranty',
			'costService',
			'costTotal',
			'fordMargin',
			'cancelValue',
			'parcelValue'
		]);
		const dateColumns = new Set<string>([
			'contractDateCreate',
			'dateStart',
			'contractDateEnd',
			'wsd',
			'contractDateUpdate',
			'cancelDate',
			'coverageStartDate',
			'coverageEndDate'
		]);
		const getCurrencyNumFmt = (country: unknown): string =>
			country === ConstantsFordProtect.BRA ? '[$-416]#,##0.00' : '[$-409]#,##0.00';

		return Object.entries(headerMap).map(([key, header]) => ({
			key,
			header,
			type: dateColumns.has(key) ? 'date' : numericColumns.has(key) ? 'number' : undefined,
			numFmt: dateColumns.has(key)
				? 'dd/mm/yyyy'
				: currencyColumns.has(key)
					? (row => getCurrencyNumFmt(row['country']))
					: undefined
		}));
	}

	private translateContractEventCode(value: unknown): string {
		switch (Number(value)) {
			case 115:
				return this.translateService.instant('TRANSLATE.FULL.REFUND');
			case 116:
				return this.translateService.instant('TRANSLATE.PARTIAL.REFUND');
			case 117:
				return this.translateService.instant('TRANSLATE.WITHIN.RISK.COVERAGE');
			default:
				return '';
		}
	}

	buttonEdit(filterContractSearchResultSimplified: FilterContractSearchResultSimplified) {
		this.contractService.setFilterContractSearchByContext(this.searchContext, this.filterContractSearch);
		this.contractService.setFilterContractSearchResultSimplifiedByContext(this.searchContext, filterContractSearchResultSimplified);
		this.router.navigate(['contract-edit']);
	}

	statusName(statusId: number): string {
		const status = this.statusFilter.find(status => status.value === statusId);
		return status ? status.label : '';
	}

	private searchSalesTypeFord() {
		this.contractService.searchSalesTypeFord()
			.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: SalesTypeFord[]) => {
				this.salesTypeFords = this.translateSalesTypeFords([this.addSalesTypeFordDefault(), ...response]);

				if (this.filterContractSearch.salesTypeFordId > 0) {
					this.salesTypeFord = this.salesTypeFords.find(salesTypeFord => salesTypeFord.id === this.filterContractSearch.salesTypeFordId) || this.addSalesTypeFordDefault();
				} else {
					this.salesTypeFord = this.addSalesTypeFordDefault();
				}
			}, (err) => {

				if (err.status === 500) {
					this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Tipo de venda não encontrado.' });
				}
			});
	}

	private translateSalesTypeFords(salesTypeFordArray: SalesTypeFord[]): SalesTypeFord[] {

		for (const item of salesTypeFordArray) {

			switch (item.description) {
				case "Varejo":
					item.description = "TRANSLATE.RETAIL";
					break;
				case "Direta":
					item.description = "TRANSLATE.DIRECT";
					break;
				case "Governo":
					item.description = "TRANSLATE.FLEET";
					break;
				case "Free Contract":
					item.description = "TRANSLATE.FREE.CONTRACT";
					break;
				case "Frota Interna":
					item.description = "TRANSLATE.INTERNAL.FLEET";
					break;
			}
		}

		return salesTypeFordArray;
	}

	private addSalesTypeFordDefault(): SalesTypeFord {
		const salesTypeFord: SalesTypeFord = new SalesTypeFord();
		salesTypeFord.id = 0;
		salesTypeFord.description = 'TRANSLATE.SELECT';

		return salesTypeFord;
	}

	private searchProductActiveCountry() {

		if (this.filterContractSearch.countries !== null) {
			if (this.filterContractSearch.countries.length > 0) {
				this.productService.getProductActiveCodeCountries(this.filterContractSearch.countries)
					.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: ProductActive[]) => {

						this.productActiveCountries = [this.addProductActiveDefault(), ...response];

						if (this.filterContractSearch.productId > 0) {
							this.productActive = this.productActiveCountries.find(product => product.id === this.filterContractSearch.productId) || this.addProductActiveDefault();

						} else {
							this.productActive = this.addProductActiveDefault();
						}
					}, (error) => {

						switch (error.status) {

							case 400:
							case 404:
							case 500:
								this.messageService.add({
									severity: 'error',
									summary: this.translateService.instant('TRANSLATE.ERROR'),
									detail: this.translateService.instant('TRANSLATE.PRODUCTS.NOT.FOUND')
								});
								break;

							case 401:
								this.messageService.add({
									severity: 'error',
									summary: this.translateService.instant('TRANSLATE.ERROR'),
									detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
								});
								break;

							default:
								this.messageService.add({
									severity: 'error',
									summary: this.translateService.instant('TRANSLATE.ERROR'),
									detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
								});
								break;
						}

					});
			} else {
				this.productActiveCountries = [];
				this.productActive = this.addProductActiveDefault();
			}
		}
	}

	private addProductActiveDefault(): ProductActive {
		const productActive: ProductActive = new ProductActive();
		productActive.id = 0;
		productActive.description = 'Selecione';
		productActive.name = 'Selecione';
		productActive.code = 'Selecione';

		return productActive;
	}

	private clearSearchResults(): void {
		this.filterContractSearch.numberPage = 0;
		this.filterContractSearchResultList = [];
		this.filterContractSearchResultTotalRecord.filterContractSearchResultSimplifiedDtos = [];
		this.filterContractSearchResultTotalRecord.totalRecord = 0;
	}

	countryChange(_event: unknown) {
		this.clearSearchResults();
		this.filterContractSearch.document = this.sanitizeDocumentSearchValue(this.filterContractSearch.document || '');
		this.hasTriedFilterSubmit = false;
		this.searchProductActiveCountry();
	}

	get hasSelectedCountries(): boolean {
		return Array.isArray(this.filterContractSearch?.countries) && this.filterContractSearch.countries.length > 0;
	}

	scheduleCountryPanelClose(countryMultiSelect: HideablePanel | null | undefined) {
		this.cancelCountryPanelClose();

		this.countryPanelCloseTimeout = setTimeout(() => {
			if (countryMultiSelect && typeof countryMultiSelect.hide === 'function') {
				countryMultiSelect.hide();
			}
			this.countryPanelCloseTimeout = null;
		}, this.countryPanelCloseDelayMs);
	}

	cancelCountryPanelClose() {
		if (this.countryPanelCloseTimeout) {
			clearTimeout(this.countryPanelCloseTimeout);
			this.countryPanelCloseTimeout = null;
		}
	}

	rangeDateLabel(): string {
		const date = new Date();
		const dateLabelYear = date.getFullYear() + 50;
		return '1970:' + dateLabelYear;
	}

	formatOdometerNumber(event: Event) {

		const input = event.target;
		if (!(input instanceof HTMLInputElement)) {
			return;
		}
		let odometereTreatment = input.value;

		odometereTreatment = odometereTreatment.replace(/\D/g, "");
		odometereTreatment = odometereTreatment.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");

		input.value = odometereTreatment;

		const valueWithoutPoint = odometereTreatment.replace(/\./g, '').replace('.', ',');
		this.zeroMileage = valueWithoutPoint ? parseInt(valueWithoutPoint, 10) : null;
	}

	showDialog(contractKey: number, productKey: number, coverageCategoryKey: number) {
		if (!this.canRequestCancellation) {
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION')
			});
			return;
		}

		this.idProductNameModal = null;
		this.chassiModal = null;
		this.customerNameModal = null;
		this.contractId = null;
		this.productKey = 0;
		this.coverageKey = 0;
		this.filterContractSearchResult = null;
		this.hasTriedCancellationSubmit = false;
		this.display = true;

		if (this.filterContractSearchResultTotalRecord !== null) {

			this.contractService.searchContractId(contractKey, productKey, coverageCategoryKey)
				.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: FilterContractSearchResult) => {

					this.filterContractSearchResult = response;
					this.idProductNameModal = this.filterContractSearchResult.productKey + " - " + this.filterContractSearchResult.productName;
					this.chassiModal = this.filterContractSearchResult.vin;
					this.customerNameModal = this.filterContractSearchResult.customerName + " " + this.filterContractSearchResult.customerLastName;
					this.customerNameModal = capitalizeWords(this.customerNameModal);
					this.contractId = contractKey;
					this.productKey = productKey;
					this.coverageKey = coverageCategoryKey;
					this.vinSaleDateComparison = this.filterContractSearchResult.vinSaleDate;
					this.dealerEmail = this.filterContractSearchResult.dnEmail;
					this.sellerEmail = this.apsPermissionService.getUserEmail() || this.filterContractSearchResult.sellerEmail;
					this.sellerCode = this.filterContractSearchResult.sellerCode;

				}, (error) => {

					switch (error.status) {

						case 400:
						case 404:
						case 500:
							this.messageService.add({
								severity: 'error',
								summary: this.translateService.instant('TRANSLATE.ERROR'),
								detail: this.translateService.instant('TRANSLATE.INVALID.CONTRACT.SEARCH.CONTACT.ADM')
							});
							break;

						case 401:
							this.messageService.add({
								severity: 'error',
								summary: this.translateService.instant('TRANSLATE.ERROR'),
								detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
							});
							break;

						default:
							this.messageService.add({
								severity: 'error',
								summary: this.translateService.instant('TRANSLATE.ERROR'),
								detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
							});
							break;
					}

				});
		}
	}

	reasonChange(event: { value: string | null }) {
		this.selectedReason = event.value;
	}

	getValueLength(value: unknown): number {
		if (value === null || value === undefined) {
			return 0;
		}

		return String(value).length;
	}

	isEmailFormatInvalid(email: string | null | undefined): boolean {
		const normalizedEmail = (email || '').trim();
		return normalizedEmail.length > 0 && !this.emailPattern.test(normalizedEmail);
	}

	getCancellationMileageErrorKey(): string {
		return this.zeroMileage !== null && this.zeroMileage <= 0
			? 'TRANSLATE.MUST.BE.GREATER.THAN.ZERO'
			: 'TRANSLATE.INFORME.MILEAGE';
	}

	private focusCancellationField(fieldId: CancellationFieldId): void {
		setTimeout(() => {
			const field = this.resolveCancellationField(fieldId);
			field?.focus();
		}, 0);
	}

	private resolveCancellationField(fieldId: CancellationFieldId): HTMLElement | null {
		const field = document.getElementById(fieldId);

		if (!field) {
			return null;
		}

		if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLButtonElement) {
			return field;
		}

		return field.querySelector<HTMLElement>('.p-dropdown, [role="combobox"], .p-dropdown-label, .p-dropdown-trigger') ?? field;
	}

	private validateCancellationForm(): CancellationFieldId | null {
		if (!this.cancellationDateModal) {
			this.cancellationDateModal = null;
			return 'cancellationDateModalSearch';
		}

		const contractCreationDate = new Date(this.vinSaleDateComparison);
		const contractCancellationDate = new Date(this.cancellationDateModal);

		if (contractCancellationDate < contractCreationDate) {
			this.cancellationDateModal = null;
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.CONTRACT.CANCELLATION.DATE.MUST.BE.AFTER.CREATION.DATE')
			});
			return 'cancellationDateModalSearch';
		}

		if (this.selectedReason === undefined || this.selectedReason === null) {
			this.selectedReason = null;
			return 'reasonDropdownSearch';
		}

		if (this.zeroMileage === null || Number.isNaN(this.zeroMileage)) {
			this.zeroMileage = null;
			return 'carMileageSearch';
		}

		if (this.zeroMileage <= 0) {
			return 'carMileageSearch';
		}

		if (!this.dealerEmail || this.dealerEmail.trim() === '') {
			this.dealerEmail = null;
			return 'dealerEmailSearch';
		}

		if (this.isEmailFormatInvalid(this.dealerEmail)) {
			return 'dealerEmailSearch';
		}

		if (!this.sellerEmail || this.sellerEmail.trim() === '') {
			this.sellerEmail = null;
			return 'sellerEmailSearch';
		}

		if (this.isEmailFormatInvalid(this.sellerEmail)) {
			return 'sellerEmailSearch';
		}

		if (this.observations === undefined || this.observations === null || this.observations.trim() === '') {
			this.observations = null;
			return 'observationsSearch';
		}

		return null;
	}

	cancelRequestButton() {
		if (!this.canRequestCancellation) {
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION')
			});
			return;
		}

		this.hasTriedCancellationSubmit = true;

		const invalidField = this.validateCancellationForm();

		if (invalidField) {
			this.focusCancellationField(invalidField);
			return;
		}

		const fallbackSellerCode = (sessionStorage.getItem('userId') || '').trim();
		const authenticatedSellerCode = (this.apsPermissionService.getUserCsid() || '').trim();
		const resolvedSellerCode = fallbackSellerCode || authenticatedSellerCode || (this.sellerCode || '').trim();
		const authenticatedSellerEmail = this.apsPermissionService.getUserEmail();
		const resolvedSellerEmail = (this.sellerEmail || authenticatedSellerEmail || '').trim().toLowerCase();
		const resolvedDealerEmail = (this.dealerEmail || '').trim().toLowerCase();

		if (!resolvedSellerEmail) {
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
			});
			return;
		}

		this.isSubmittingCancellation = true;
		this.blockedUi = true;

		this.carMileage = this.zeroMileage!;
		const cancellationDate = new Date(this.cancellationDateModal!);
		const formattedDate = cancellationDate.toISOString().split('T')[0]; // Format to YYYY-MM-DD
		const cancellationDebugContext = {
			stage: 'request-cancellation',
			contractId: this.contractId,
			productKey: this.productKey,
			coverageKey: this.coverageKey,
			dealerEmail: resolvedDealerEmail,
			sellerEmail: resolvedSellerEmail,
			sellerCode: resolvedSellerCode,
			jwtEmail: authenticatedSellerEmail,
			jwtCsid: authenticatedSellerCode,
			sessionUserId: fallbackSellerCode,
			origin: window.location.origin
		};

		const contractCancelInput = new ContractCancelInput();
		contractCancelInput.contractId = this.contractId!;
		contractCancelInput.productId = this.productKey;
		contractCancelInput.coverageId = this.coverageKey;
		contractCancelInput.cancelDate = formattedDate; // Use the formatted date
		contractCancelInput.cancellationReason = this.selectedReason!;
		contractCancelInput.kilometers = this.carMileage;
		contractCancelInput.remarks = this.observations!;
		contractCancelInput.dealerEmail = resolvedDealerEmail;
		contractCancelInput.sellerEmail = resolvedSellerEmail;
		contractCancelInput.sellerCode = resolvedSellerCode;

		this.contractService.determineCancelValues(contractCancelInput).pipe(
			tap(cancelBody => {
				cancelBody.sellerCode = resolvedSellerCode;
				cancelBody.createUser = resolvedSellerCode;
				cancelBody.updateUser = resolvedSellerCode;
				cancelBody.dealerEmail = contractCancelInput.dealerEmail;
				cancelBody.sellerEmail = contractCancelInput.sellerEmail;
			}),
			switchMap(cancelBody => this.contractService.cancelContract(cancelBody))
		).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({

			next: () => {

				this.messageService.add({
					severity: 'success',
					summary: this.translateService.instant('TRANSLATE.SUCCESS'),
					detail: this.translateService.instant('TRANSLATE.CANCELLATION.REQUEST.SUCCESSFUL')
				});
				this.blockedUi = false;
				this.isSubmittingCancellation = false;
				this.cleanFilters();
				this.filterValidation();

			},

			error: (error) => {

				this.blockedUi = false;
				this.isSubmittingCancellation = false;
				this.showCancellationError(error, cancellationDebugContext);

			}

		});

	}

	cleanFilters() {
		this.display = false;
		this.isSubmittingCancellation = false;
		this.hasTriedCancellationSubmit = false;
		this.cancellationDateModal = null;
		this.selectedReason = null;
		this.zeroMileage = null;
		this.carMileage = null;
		this.observations = null;
		this.dealerEmail = null;
		this.sellerEmail = null;
	}

	clearModalCancel() {

		this.display = false;
		this.isSubmittingCancellation = false;
		this.hasTriedCancellationSubmit = false;
		this.idProductNameModal = null;
		this.chassiModal = null;
		this.customerNameModal = null;
		this.contractId = null;
		this.productKey = 0;
		this.coverageKey = 0;
		this.filterContractSearchResult = null;
		this.cancellationDateModal = null;
		this.selectedReason = null;
		this.carMileage = null;
		this.zeroMileage = null;
		this.observations = null;
		this.dealerEmail = null;
		this.sellerEmail = null;
		this.sellerCode = null;
	}

	// MODAL CONTROL - UNDO CANCELLATION
	openModalUndoCancel(contractId: number, vin: string, productId: number) {

		this.displayUndoCancel = true;
		this.contractIDUndoCancellation = contractId;
		this.vinUndoCancellation = vin;
		this.productIDUndoCancellation = productId;
	}

	undoButtonCancellation() {

		if (this.contractIDUndoCancellation && this.vinUndoCancellation && this.productIDUndoCancellation) {

			this.blockedUi = true;
			this.disableReactivateContract = true;

			// O serviço criado será implementado depois, que o mesmo já existir no backend
			this.contractService.undoCancellation(this.contractIDUndoCancellation, this.vinUndoCancellation, this.productIDUndoCancellation)
				.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
					next: () => {

						this.blockedUi = false;
						this.disableReactivateContract = false;
						this.clearFieldsUndoCancel();
						this.filterValidation();

						this.messageService.add({
							severity: 'success',
							summary: this.translateService.instant('TRANSLATE.SUCCESS'),
							detail: this.translateService.instant('TRANSLATE.REACTIVE.CONTRACT.SUCCESS')
						});
					}, error: (error) => {

						this.blockedUi = false;
						this.disableReactivateContract = false;
						switch (error.status) {

							case 400:
							case 404:
							case 500:

								this.messageService.add({
									severity: 'error',
									summary: this.translateService.instant('TRANSLATE.ERROR'),
									detail: this.translateService.instant('TRANSLATE.REACTIVE.CONTRACT.ERROR')
								});
								break;

							case 401:

								this.messageService.add({
									severity: 'error',
									summary: this.translateService.instant('TRANSLATE.ERROR'),
									detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
								});
								break;

							default:

								this.messageService.add({
									severity: 'error',
									summary: this.translateService.instant('TRANSLATE.ERROR'),
									detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
								});
								break;
						}
					}
				});

		}

	}

	clearFieldsUndoCancel() {
		this.displayUndoCancel = false;
		this.contractIDUndoCancellation = undefined;
		this.vinUndoCancellation = undefined;
		this.productIDUndoCancellation = undefined;
	}

	//MODAL CONTROL - download PDF
	retrievingContractId(contractNumber: number, _country?: string, _productCode?: string) {
		this.contractId = contractNumber;
		this.contractCountryCode = _country ?? '';
		this.prebContract = _productCode ?? '';
		this.termFileId = null;
		this.termTypeFile = null;
		this.ticketFileId = null;
		this.ticketTypeFile = null;
		this.documentType = null;
		this.showDocumentTypeError = false;
	}

	searchingTermByContractIdAndFileType(contractId: number) {
		return this.contractService.searchByContractIdAndFileType(contractId, this.getTermFileTypesForSelectedContract())
			.pipe(
				tap((response: Term | null) => {
					this.termFileId = response?.fileId ?? null;
					this.termTypeFile = response?.fileType ?? null;
				}),
				catchError(() => {
					this.termFileId = null;
					this.termTypeFile = null;
					return of(null);
				})
			);
	}

	searchingForTicketByContractIdAndFileType(contractId: number) {
		return this.contractService.searchByContractIdAndFileType(contractId, fileTypeTicket().map(ft => ft as number))
			.pipe(
				tap((response: Term | null) => {
					this.ticketFileId = response?.fileId ?? null;
					this.ticketTypeFile = response?.fileType ?? null;
				}),
				catchError(() => {
					this.ticketFileId = null;
					this.ticketTypeFile = null;
					return of(null);
				})
			);
	}

	showModalPdf() {
		if (this.hasValidContractId()) {
			this.blockedUi = true;
			this.showDocumentTypeError = false;
			forkJoin([
				this.searchingTermByContractIdAndFileType(this.contractId!),
				this.searchingForTicketByContractIdAndFileType(this.contractId!)
			])
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: () => {
						this.blockedUi = false;
						this.displayModalPdf = true;
					},
					error: () => {
						this.blockedUi = false;
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
					}
				});
			return;
		}

		this.messageService.add({
			severity: 'error',
			summary: this.translateService.instant('TRANSLATE.ERROR'),
			detail: this.translateService.instant('TRANSLATE.CONTRACT.NOT.FOUND')
		});
	}

	handleGeneratePdf(): void {
		if (this.shouldOpenPdfModal()) {
			this.showModalPdf();
			return;
		}

		this.downloadTermImmediately();
	}

	opDocument(documentType: DocumentType | null) {
		this.documentType = documentType;
		this.showDocumentTypeError = false;
	}

	private hasValidContractId(): boolean {
		return this.contractId !== null && this.contractId > 0;
	}

	private hasTermFileAvailable(): boolean {
		return this.termFileId !== null && this.termFileId > 0;
	}

	hasTermDownloadOption(): boolean {
		return this.hasTermFileAvailable() || (this.shouldDownloadTermByContractId() && this.hasValidContractId());
	}

	private hasTicketFileAvailable(): boolean {
		return this.ticketFileId !== null && this.ticketFileId > 0;
	}

	private getTermFileTypesForSelectedContract(): number[] {
		switch (this.contractCountryCode) {
			case ConstantsFordProtect.BRA:
				return [
					FileType.PURCHASE_TERM as number,
					FileType.LICENSE_TERM as number,
					FileType.CONTRACT_SUCCESS_ADHESION_DEALER_MAINTENANCE as number,
				];
			case ConstantsFordProtect.ARG:
				return [
					FileType.CONTRACT_SUCCESS_ARG as number,
					FileType.CONTRACT_SUCCESS_DEALER_MAINTENANCE_ARGENTINA as number,
				];
			case ConstantsFordProtect.CHL:
				return [FileType.CONTRACT_SUCCESS_CH as number];
			case ConstantsFordProtect.COL:
				return [FileType.CONTRACT_SUCCESS_CO as number];
			default:
				return [FileType.PURCHASE_TERM as number, FileType.LICENSE_TERM as number];
		}
	}

	private isContractIdTermFileType(fileType: number | null): boolean {
		return fileType === FileType.CONTRACT_SUCCESS_ARG
			|| fileType === FileType.CONTRACT_SUCCESS_ADHESION_DEALER_MAINTENANCE
			|| fileType === FileType.CONTRACT_SUCCESS_DEALER_MAINTENANCE_ARGENTINA;
	}

	private isSelectedPrebContract(): boolean {
		return this.prebContract === ConstantsFordProtect.PREB;
	}

	private isBrazilPrebContract(): boolean {
		return this.contractCountryCode === ConstantsFordProtect.BRA && this.isSelectedPrebContract();
	}

	private shouldOpenPdfModal(): boolean {
		return this.isBrazilPrebContract();
	}

	private shouldDownloadTermByContractId(): boolean {
		return this.isSelectedPrebContract() || this.isContractIdTermFileType(this.termTypeFile);
	}

	private hasAnyDownloadableDocument(): boolean {
		return this.hasTermFileAvailable() || this.hasTicketFileAvailable();
	}

	private downloadTermImmediately(): void {
		if (this.blockedUi) {
			return;
		}

		if (!this.hasValidContractId()) {
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.CONTRACT.NOT.FOUND')
			});
			return;
		}

		this.showDocumentTypeError = false;
		this.isDownloadingPdf = true;
		this.blockedUi = true;

		this.searchingTermByContractIdAndFileType(this.contractId!)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					if (!this.hasTermDownloadOption()) {
						this.showUnavailableDocumentMessage(this.DOCUMENT_TYPE_TERM);
						this.isDownloadingPdf = false;
						this.blockedUi = false;
						return;
					}

					this.downloadResolvedTerm();
				},
				error: () => {
					this.isDownloadingPdf = false;
					this.blockedUi = false;
					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
					});
				}
			});
	}

	private downloadResolvedTerm(): void {
		if (this.shouldDownloadTermByContractId()) {
			this.downloadContractIdTerm();
			return;
		}

		this.termGeneration = new TermGeneration(this.contractId!, this.termFileId!);

		this.contractService.createFileTerm(this.termGeneration)
			.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
				next: (blob) => {
					const fileName = this.getFileName(this.termTypeFile!);
					this.forceDownload(blob, fileName);

					this.displayModalPdf = false;
					this.clearModalPdf();

					this.messageService.add({
						severity: 'success',
						summary: this.translateService.instant('TRANSLATE.SUCCESS'),
						detail: this.translateService.instant('TRANSLATE.DOWNLOAD.TERM.SUCCESSFULLY')
					});
					this.isDownloadingPdf = false;
					this.blockedUi = false;
				}, error: (error: HttpErrorResponse) => {

					this.clearModalPdf();
					this.showDownloadError(error, this.DOCUMENT_TYPE_TERM);
					this.isDownloadingPdf = false;
					this.blockedUi = false;
				}
			});
	}

	downloadPdf() {
		// Prevent concurrent downloads
		if (this.blockedUi) {
			return;
		}

		if (!this.documentType) {
			this.documentType = null;
			this.showDocumentTypeError = true;
			this.isDownloadingPdf = false;
			return;
		}

		this.showDocumentTypeError = false;
		this.isDownloadingPdf = true;

		this.blockedUi = true;

		if (this.documentType === this.DOCUMENT_TYPE_TERM) {
			if (!this.hasValidContractId() || !this.hasTermDownloadOption()) {
				this.showUnavailableDocumentMessage(this.DOCUMENT_TYPE_TERM);
				this.isDownloadingPdf = false;
				this.blockedUi = false;
				return;
			}

			this.downloadResolvedTerm();

		} else if (this.documentType === this.DOCUMENT_TYPE_TICKET) {
			if (!this.hasValidContractId() || !this.hasTicketFileAvailable()) {
				this.showUnavailableDocumentMessage(this.DOCUMENT_TYPE_TICKET);
				this.isDownloadingPdf = false;
				this.blockedUi = false;
				return;
			}

			this.contractService.getPdfFile(this.contractId!, this.ticketFileId!)
				.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
					next: (blob) => {
						const fileName = this.getFileName(this.ticketTypeFile!);
						this.forceDownload(blob, fileName);

						this.displayModalPdf = false;
						this.clearModalPdf();

						this.messageService.add({
							severity: 'success',
							summary: this.translateService.instant('TRANSLATE.SUCCESS'),
							detail: this.translateService.instant('TRANSLATE.DOWNLOAD.TICKET.SUCCESSFULLY')
						});
						this.isDownloadingPdf = false;
						this.blockedUi = false;
					},
					error: (error: HttpErrorResponse) => {
						this.showDownloadError(error, this.DOCUMENT_TYPE_TICKET);
						this.isDownloadingPdf = false;
						this.blockedUi = false;
					}
				});
		} else {
			this.documentType = null;
			this.isDownloadingPdf = false;
			this.blockedUi = false;
		}
	}

	private getDocumentTypeLabel(documentType: DocumentType): string {
		return documentType === this.DOCUMENT_TYPE_TERM
			? this.translateService.instant('TRANSLATE.TERM')
			: this.translateService.instant('TRANSLATE.TICKET');
	}

	private showUnavailableDocumentMessage(documentType: DocumentType): void {
		const documentLabel = this.getDocumentTypeLabel(documentType);
		this.messageService.add({
			severity: 'error',
			summary: this.translateService.instant('TRANSLATE.ERROR'),
			detail: `${documentLabel} não está disponível para este contrato.`
		});
	}

	private showDownloadError(error: HttpErrorResponse, documentType: DocumentType): void {
		if (error.status === 401) {
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
			});
			return;
		}

		if (error.status === 400 || error.status === 404 || error.status === 500) {
			this.showUnavailableDocumentMessage(documentType);
			return;
		}

		this.messageService.add({
			severity: 'error',
			summary: this.translateService.instant('TRANSLATE.ERROR'),
			detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
		});
	}

	private downloadContractIdTerm(): void {
		this.contractService.downloadFile(this.contractId!)
			.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
				next: (response) => {
					const fileName = this.termTypeFile !== null
						? this.getFileName(this.termTypeFile)
						: FileType.CONTRACT_TERMS_NAME;
					if (!this.forceBase64Download(response.pdfByteArray, fileName)) {
						this.clearModalPdf();
						this.showInvalidPdfSourceError();
						this.isDownloadingPdf = false;
						this.blockedUi = false;
						return;
					}

					this.displayModalPdf = false;
					this.clearModalPdf();

					this.messageService.add({
						severity: 'success',
						summary: this.translateService.instant('TRANSLATE.SUCCESS'),
						detail: this.translateService.instant('TRANSLATE.DOWNLOAD.TERM.SUCCESSFULLY')
					});
					this.isDownloadingPdf = false;
					this.blockedUi = false;
				},
				error: (error: HttpErrorResponse) => {
					this.clearModalPdf();
					this.showDownloadError(error, this.DOCUMENT_TYPE_TERM);
					this.isDownloadingPdf = false;
					this.blockedUi = false;
				}
			});
	}

	isDownloadFileTerm(): boolean {
		return this.contractId !== null && this.termFileId !== null && this.contractId > 0 && this.termFileId > 0;
	}

	downloadFileTerm() {
		setTimeout(() => {
			// Prevent concurrent downloads
			if (this.blockedUi) {
				return;
			}

			if (this.isDownloadFileTerm()) {
				this.termGeneration = new TermGeneration(this.contractId!, this.termFileId!);
				this.blockedUi = true;

				this.contractService.createFileTerm(this.termGeneration).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
					(blob) => {
						const fileName = this.getFileName(this.termTypeFile!);
						this.forceDownload(blob, fileName);

						this.displayModalPdf = false;
						this.clearModalPdf();

						this.messageService.add({
							severity: 'success',
							summary: this.translateService.instant('TRANSLATE.SUCCESS'),
							detail: this.translateService.instant('TRANSLATE.DOWNLOAD.TERM.SUCCESSFULLY'),
						});
						this.blockedUi = false;
					},
					(error) => {
						switch (error.status) {
							case 400:
							case 404:
							case 500:
								this.messageService.add({
									severity: 'error',
									summary: this.translateService.instant('TRANSLATE.ERROR'),
									detail: this.translateService.instant('TRANSLATE.DOWNLOAD.COULD.NOT.BE.COMPLETED'),
								});
								break;
							case 401:
								this.messageService.add({
									severity: 'error',
									summary: this.translateService.instant('TRANSLATE.ERROR'),
									detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE'),
								});
								break;
							default:
								this.messageService.add({
									severity: 'error',
									summary: this.translateService.instant('TRANSLATE.ERROR'),
									detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR'),
								});
								break;
						}
						this.blockedUi = false;
					}
				);
			} else {
				this.messageService.add({
					severity: 'error',
					summary: this.translateService.instant('TRANSLATE.ERROR'),
					detail: this.translateService.instant('TRANSLATE.CONTRACT.NOT.FOUND'),
				});
			}
		}, 1000);
	}

	downloadTermInPdf() {
		setTimeout(() => {
			// Prevent concurrent downloads
			if (this.blockedUi) {
				return;
			}

			if (!this.isDownloadFileTerm()) {
				this.showUnavailableDocumentMessage(this.DOCUMENT_TYPE_TERM);
				return;
			}

			this.blockedUi = true;
			this.termGeneration = new TermGeneration(this.contractId!, this.termFileId!);

			this.contractService.createFileTerm(this.termGeneration)
				.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
					(blob) => {

						const fileName = this.getFileName(this.termTypeFile!);
						this.forceDownload(blob, fileName);

						this.clearModalPdf();

						this.messageService.add({
							severity: 'success',
							summary: this.translateService.instant('TRANSLATE.SUCCESS'),
							detail: this.translateService.instant('TRANSLATE.DOWNLOAD.TERM.SUCCESSFULLY')
						});

						this.blockedUi = false;
					},
					(error: HttpErrorResponse) => {

						this.clearModalPdf();
						this.showDownloadError(error, this.DOCUMENT_TYPE_TERM);
						this.blockedUi = false;
					}
				);
		}, 1000);
	}

	getFileName(typeFile: number): string {

		switch (typeFile) {

			case FileType.PURCHASE_TERM:
				return FileType.PURCHASE_TERM_NAME;

			case FileType.LICENSE_TERM:
				return FileType.LICENSE_TERM_NAME;

			case FileType.TICKET:
				return FileType.TICKET_NAME;

			case FileType.UPDATE_TICKET:
				return FileType.UPDATE_TICKET_NAME;

			case FileType.CANCELLATION_TICKET:
				return FileType.CANCELLATION_TICKET_NAME;

			case FileType.CONTRACT_SUCCESS_CO:
				return FileType.CONTRACT_SUCCESS_CO_NAME;

			case FileType.CONTRACT_SUCCESS_CH:
				return FileType.CONTRACT_SUCCESS_CH_NAME;

			case FileType.CONTRACT_SUCCESS_ARG:
				return FileType.CONTRACT_TERMS_NAME_ARG;

			case FileType.CONTRACT_SUCCESS_ADHESION_DEALER_MAINTENANCE:
				return FileType.CONTRACT_SUCCESS_ADHESION_DEALER_MAINTENANCE_NAME;
			case FileType.CONTRACT_SUCCESS_DEALER_MAINTENANCE_ARGENTINA:
				return FileType.CONTRACT_SUCCESS_DEALER_MAINTENANCE_ARGENTINA_NAME;

			default:
				return 'arquivo_generico.pdf';

		}

	}

	forceDownload(blob: Blob, fileName: string) {

		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = fileName;
		link.click();
		window.URL.revokeObjectURL(url);
	}

	private forceBase64Download(source: string, fileName: string): boolean {
		const blob = this.base64PdfToBlob(source);
		if (!blob) {
			return false;
		}

		this.forceDownload(blob, fileName);
		return true;
	}

	private base64PdfToBlob(source: string): Blob | null {
		const normalizedSource = source.trim().replace(/^['"]|['"]$/g, '').replace(/\s+/g, '');
		const dataUrlMatch = normalizedSource.match(/^data:[^;,]+;base64,([^#?]+)$/i);
		const rawBase64Payload = dataUrlMatch?.[1]
			?? (/^[A-Za-z0-9+/=_-]+$/.test(normalizedSource) ? normalizedSource : null);
		const base64Payload = rawBase64Payload
			? this.normalizeBase64Payload(rawBase64Payload)
			: null;

		if (!base64Payload) {
			return null;
		}

		let binary = '';
		try {
			binary = window.atob(base64Payload);
		} catch {
			return null;
		}

		const bytes = new Uint8Array(binary.length);

		for (let index = 0; index < binary.length; index += 1) {
			bytes[index] = binary.charCodeAt(index);
		}

		if (!binary.startsWith('%PDF')) {
			return null;
		}

		return new Blob([bytes], { type: 'application/pdf' });
	}

	private normalizeBase64Payload(source: string): string | null {
		const normalized = source.replace(/-/g, '+').replace(/_/g, '/');
		const remainder = normalized.length % 4;

		if (remainder === 1) {
			return null;
		}

		return remainder === 0
			? normalized
			: normalized.padEnd(normalized.length + (4 - remainder), '=');
	}

	private showInvalidPdfSourceError(): void {
		this.messageService.add({
			severity: 'error',
			summary: this.translateService.instant('TRANSLATE.ERROR'),
			detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
		});
	}

	clearModalPdf() {

		this.displayModalPdf = false;
		this.documentType = null;
		this.showDocumentTypeError = false;
		this.isDownloadingPdf = false;
		this.contractId = null;
		this.termFileId = null;
		this.termTypeFile = null;
		this.termGeneration = null;
		this.ticketFileId = null;
		this.ticketTypeFile = null;
		this.fileNameType = '';
		this.contractCountryCode = '';
		this.prebContract = '';
	}

}